import React, { useState, useRef, useEffect } from 'react';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import CircularProgress from '@mui/material/CircularProgress';
import Modal from '@mui/material/Modal';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import Button from '@mui/material/Button';
import { makeStyles } from '@mui/styles';
import MinimizeIcon from '@mui/icons-material/Minimize';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import DoDisturbIcon from '@mui/icons-material/DoDisturb';
import useAuth from 'src/hooks/useAuth';
import toast from 'react-hot-toast';
import { chatApi } from 'src/api/chatApi';
import { userApi } from 'src/api/userApi';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import DoneIcon from '@mui/icons-material/Done';
// Project selector temporarily hidden — first org project is auto-selected
// import EmbeddedProjectSelector from './EmbeddedProjectSelector';
import CategorySelector, { Category } from './CategorySelector';
import { casesApi } from 'src/api/casesApi';
import { organizationApi } from 'src/api/organizationApi';
import { projectApi } from 'src/api/projectApi';
import { isWithinWorkingHoursOverlap, getNextWorkingHoursMessage, WorkingHours } from 'src/utils/workingHours';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DeleteIcon from '@mui/icons-material/Delete';
import GetAppIcon from '@mui/icons-material/GetApp';
import ImageIcon from '@mui/icons-material/Image';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DescriptionIcon from '@mui/icons-material/Description';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import TypingIndicator from './TypingIndicator';
import FeedbackCollection from './FeedbackCollection';
import { FeedbackData } from 'src/types/chat';
import { ImagePreviewer } from 'src/components/widgets/modal/ImagePreviewer';
import { useUserProfile } from 'src/contexts/UserProfileContext';
import logger from 'src/utils/logger';

const useStyles = makeStyles((theme) => ({
    chatContainer: {
        position: 'fixed',
        bottom: theme.spacing(1),
        right: theme.spacing(3),
        width: '400px',
        height: '700px',
        zIndex: 999,
        display: 'flex',
        flexDirection: 'column',
        boxShadow: theme.shadows[8],
        borderRadius: theme.spacing(1),
        overflow: 'hidden',
        transition: 'height 0.3s ease-in-out',
    },
    chatContainerMinimized: {
        height: '50px',
    },
    chatHeader: {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        padding: theme.spacing(1, 2),
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        height: '50px',
    },
    chatBody: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: theme.palette.background.paper,
        overflow: 'hidden',
    },
    messagesContainer: {
        flex: 1,
        overflow: 'auto',
        padding: theme.spacing(1),
        backgroundColor: '#f5f5f5',
    },
    inputContainer: {
        padding: theme.spacing(1),
        backgroundColor: theme.palette.background.paper,
        borderTop: `1px solid ${theme.palette.divider}`,
    },
    messageInput: {
        '& .MuiOutlinedInput-root': {
            '& textarea': {
                resize: 'none',
                minHeight: '20px',
                maxHeight: '80px', // Approximately 4 lines
                overflow: 'auto',
            },
        },
    },
    sendButton: {
        marginTop: theme.spacing(1),
        alignSelf: 'flex-end',
    },
    message: {
        marginBottom: theme.spacing(1),
        display: 'flex',
        alignItems: 'flex-start',
        gap: theme.spacing(1),
    },
    messageUser: {
        flexDirection: 'row-reverse',
        // gap: theme.spacing(0.5),
        '& $messageBubble': {
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
        },
    },
    messageBubble: {
        backgroundColor: theme.palette.grey[200],
        borderRadius: theme.spacing(2),
        padding: theme.spacing(1, 2),
        maxWidth: '80%',
        wordBreak: 'break-word',
        minWidth: '200px',
    },
    systemMessageBubble: {
        backgroundColor: theme.palette.grey[100],
        color: theme.palette.text.primary,
        borderRadius: theme.spacing(2),
        padding: theme.spacing(1, 2),
        maxWidth: '70%',
        wordBreak: 'break-word',
        border: `1px solid ${theme.palette.divider}`,
    },
    messageTime: {
        fontSize: '0.75rem',
        float: 'right',
        marginTop: theme.spacing(0.5),
    },
    projectChip: {
        marginLeft: theme.spacing(1),
    },
    emptyState: {
        display: 'flex',
        flex: 1,
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        textAlign: 'center',
        color: theme.palette.text.secondary,
        padding: theme.spacing(2),
    },
    projectSelectionContainer: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: theme.palette.background.paper,
    },
    loadingContainer: {
        display: 'flex',
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        flexDirection: 'column',
        gap: theme.spacing(2),
    },
    workingHoursNotice: {
        backgroundColor: theme.palette.warning.light,
        border: `1px solid ${theme.palette.warning.main}`,
        borderRadius: theme.spacing(1),
        padding: theme.spacing(1.5),
        margin: theme.spacing(1),
        color: theme.palette.warning.contrastText,
    },
    inputContainerDisabled: {
        padding: theme.spacing(1),
        backgroundColor: theme.palette.grey[100],
        borderTop: `1px solid ${theme.palette.divider}`,
        opacity: 0.6,
    },
    interactiveComponent: {
        marginTop: theme.spacing(2),
        padding: theme.spacing(1.5),
        backgroundColor: theme.palette.background.paper,
        borderRadius: theme.spacing(1),
        boxShadow: theme.shadows[1],
        border: `1px solid ${theme.palette.divider}`,
        marginLeft: theme.spacing(1),
        marginRight: theme.spacing(1),
    },
    attachmentContainer: {
        marginTop: theme.spacing(1),
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(0.5),
    },
    attachmentChip: {
        maxWidth: '200px',
        '& .MuiChip-label': {
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
        },
    },
    attachmentIcon: {
        fontSize: '1rem',
        marginRight: theme.spacing(0.5),
    },
    fileInput: {
        display: 'none',
    },
    attachmentButton: {
        marginRight: theme.spacing(1),
    },
    attachmentPreview: {
        marginTop: theme.spacing(1),
        padding: theme.spacing(1),
        backgroundColor: theme.palette.grey[50],
        borderRadius: theme.spacing(1),
        border: `1px solid ${theme.palette.divider}`,
        maxHeight: '250px',
        overflow: 'auto',
    },
    attachmentPreviewItem: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: theme.spacing(0.5),
        marginBottom: theme.spacing(0.5),
        backgroundColor: theme.palette.background.paper,
        borderRadius: theme.spacing(0.5),
        border: `1px solid ${theme.palette.divider}`,
    },
    attachmentPreviewInfo: {
        display: 'flex',
        alignItems: 'center',
        flex: 1,
        minWidth: 0,
    },
    attachmentPreviewName: {
        marginLeft: theme.spacing(0.5),
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        flex: 1,
    },
}));

interface Message {
    id: string;
    text: string;
    sender: string;
    timestamp: Date;
    senderId?: string;
    isMessagedBySupportUser?: boolean;
    readBy?: any[];
    caseKey?: string;
    isSystemMessage?: boolean;
    attachments?: Attachment[] | string[];
}

interface Attachment {
    id: string;
    name: string;
    size: number;
    type: string;
    url: string;
}

interface Project {
    id: string;
    name: string;
    description?: string;
}



interface DockableChatBoxProps {
    project?: Project;
    session?: any;
    onClose: () => void;
    onMinimize?: () => void;
    existingSession?: any;
}

const DockableChatBox: React.FC<DockableChatBoxProps> = ({
    project,
    session,
    onClose,
    onMinimize,
    existingSession,
}) => {
    const classes = useStyles();
    const [messages, setMessages] = useState<any>([]);
    const [inputMessage, setInputMessage] = useState('');
    const [isMinimized, setIsMinimized] = useState(false);
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const messagesContainerRef = useRef<HTMLDivElement>(null);
    const [lastReadTime, setLastReadTime] = useState<string | null>(null);
    const wsRef = useRef<WebSocket | null>(null);
    const lastReadTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const [page, setPage] = useState(0);
    const [pageSize] = useState(20);
    const { user } = useAuth();
    const { userData } = useUserProfile();

    // New state for handling category selection, project selection and session creation
    const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
    const [selectedProject, setSelectedProject] = useState<Project | null>(project || null);
    const [chatSession, setChatSession] = useState<any>(session?.data || null);
    const [isCreatingSession, setIsCreatingSession] = useState(false);
    const [organizationId, setOrganizationId] = useState<string | null>(localStorage.getItem('organizationId'));
    const [problemDescription, setProblemDescription] = useState<string>('');
    const [isWithinWorkingHours, setIsWithinWorkingHours] = useState<boolean>(true);
    const [workingHoursMessage, setWorkingHoursMessage] = useState<string>('');
    const [globalWorkingHours, setGlobalWorkingHours] = useState<WorkingHours | null>(null);
    const [workingHoursLoading, setWorkingHoursLoading] = useState<boolean>(true);
    const [lastSupportUserId, setLastSupportUserId] = useState<string | null>(null);

    // New state for interactive chat flow
    const [showCategorySelector, setShowCategorySelector] = useState<boolean>(true);

    // State for managing attachments
    const [attachments, setAttachments] = useState<Attachment[]>([]);
    const [isUploading, setIsUploading] = useState<boolean>(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // State for attachment modal
    const [isAttachmentModalOpen, setIsAttachmentModalOpen] = useState(false);
    const [selectedAttachment, setSelectedAttachment] = useState<{ url: string; type?: string } | null>(null);

    // State for tracking uploading files (for paste functionality)

    // State for typing indicators
    const [isTyping, setIsTyping] = useState(false);
    const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    // State for burger menu
    const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
    const isMenuOpen = Boolean(menuAnchorEl);

    // State for feedback collection
    const [showFeedbackCollection, setShowFeedbackCollection] = useState<boolean>(false);

    const scrollToBottom = (smooth = true): void => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({
                behavior: smooth ? 'smooth' : 'auto',
                block: 'end',
            });
        }
    };



    // Add welcome message when component mounts
    useEffect(() => {
        if (!existingSession && messages.length === 0) {
            const welcomeMessage = {
                type: 'message',
                id: 'welcome-' + Date.now(),
                text: 'Hello! Welcome to our support chat. How can we help you today?',
                sender: 'Support',
                senderId: 'system',
                timestamp: new Date(),
                isMessagedBySupportUser: true,
                readBy: [],
                caseKey: '',
                isSystemMessage: true
            };
            setMessages([welcomeMessage]);
            setTimeout(() => scrollToBottom(), 100);
        } else if (existingSession && messages.length === 0) {
            // Add a continuation message for existing sessions
            const continuationMessage = {
                type: 'message',
                id: 'continuation-' + Date.now(),
                text: `Welcome back! You're continuing your conversation about case: ${existingSession.caseKey}`,
                sender: 'Support',
                senderId: 'system',
                timestamp: new Date(),
                isMessagedBySupportUser: true,
                readBy: [],
                caseKey: existingSession.caseKey,
                isSystemMessage: true
            };
            setMessages([continuationMessage].reverse());
            wsRef.current?.send(JSON.stringify(continuationMessage));
            setTimeout(() => scrollToBottom(), 100);
        }
    }, [existingSession]);

    // Fetch global working hours from API
    useEffect(() => {
        const fetchGlobalWorkingHours = async (retryCount = 0) => {
            try {
                setWorkingHoursLoading(true);
                const response = await chatApi.getGlobalWorkingHours();
                if (response && response.data) {

                    const workingHours: any = {
                        startTime: response.data.startTime,
                        endTime: response.data.endTime,
                        timezone: response.data.timezone
                    };
                    setGlobalWorkingHours(workingHours);
                    setWorkingHoursLoading(false);
                } else {
                    throw new Error('No working hours data received from API');
                }
            } catch (error) {
                console.error('Error fetching global working hours:', error);

                // Retry once after 2 seconds if this is the first failure
                if (retryCount === 0) {
                    setTimeout(() => fetchGlobalWorkingHours(1), 2000);
                } else {
                    // After retry failure, use default values and show warning
                    console.warn('Using default working hours due to API failure');
                    toast.error('Unable to fetch current working hours. Using default schedule.');
                }

                // Keep globalWorkingHours as null to use default values
            } finally {
                // Set loading to false only after final attempt (success or final failure)
                if (retryCount > 0) {
                    setWorkingHoursLoading(false);
                }
            }
        };

        fetchGlobalWorkingHours();
    }, []);

    useEffect(() => {
        if (workingHoursLoading) return undefined;

        const checkWorkingHours = () => {
            const withinHours = isWithinWorkingHoursOverlap(globalWorkingHours || undefined);
            setIsWithinWorkingHours(withinHours);
            if (!withinHours) {
                setWorkingHoursMessage(getNextWorkingHoursMessage(globalWorkingHours || undefined));
            }
        };

        checkWorkingHours();

        const interval = setInterval(checkWorkingHours, 60000);

        return () => clearInterval(interval);
    }, [globalWorkingHours, workingHoursLoading]);

    useEffect(() => {
        if (existingSession) {
            const sessionPayload = {
                data: {
                    "sessionStatus": "OPEN",
                    "createdAt": existingSession.createdAt,
                    "caseKey": existingSession.caseKey,
                    "id": existingSession.id,
                    "orgId": existingSession.orgId,
                    "customerId": existingSession.customerId,
                    "projectId": existingSession.projectId,
                    "supportUsers": existingSession?.supportUsers,
                    "unreadMessages": existingSession?.unreadMessages
                }
            }
            setLastSupportUserId(existingSession?.supportUsers?.[0]?.id || null);
            setChatSession(sessionPayload);

            // Skip the interactive flow when we have an existing session
            setShowCategorySelector(false);

            // Fetch and set the selected project from the existing session
            if (existingSession.projectId) {
                projectApi.getProjectDetailsById(existingSession.projectId)
                    .then((response) => {
                        if (response?.data) {
                            setSelectedProject(response.data);

                            // If the project has a category, set it as well
                            if (response.data.category) {
                                setSelectedCategory(response.data.category);
                            }
                        }
                    })
                    .catch((error) => {
                        console.error('Error fetching project details:', error);
                        // Fallback to minimal project object
                        setSelectedProject({
                            id: existingSession.projectId,
                            name: 'Existing Project',
                        } as Project);
                    });
            }
        }
    }, [existingSession]);

    useEffect(() => {
        if (!organizationId && userData?.organization?.id) {
            setOrganizationId(userData.organization.id);
            localStorage.setItem('organizationId', userData.organization.id);
        }
    }, [organizationId, userData?.organization?.id]);

    // Handle category selection — auto-selects first project (project list UI hidden)
    const handleCategorySelect = async (category: Category) => {
        setSelectedCategory(category);
        setShowCategorySelector(false);

        const categoryMessage = {
            id: 'category-' + Date.now(),
            text: `Category: ${category.name}`,
            sender: user?.name || 'You',
            senderId: user?.id || 'user',
            timestamp: new Date(),
            isMessagedBySupportUser: false,
            readBy: [],
            caseKey: '',
            isSystemMessage: true
        };

        setMessages(prev => [...prev, categoryMessage]);
        setTimeout(() => scrollToBottom(), 100);

        try {
            const response = await organizationApi.getAllProjects(0, 25, '');
            if (response?.status !== 200) {
                toast.error('Failed to load projects');
                return;
            }
            const projects = response?.data?.data || response?.data || [];
            const first = Array.isArray(projects) ? projects[0] : null;
            if (!first?.id) {
                toast.error('No projects available.');
                return;
            }

            setSelectedProject({ id: first.id, name: first.name ?? '' });

            setTimeout(() => {
                const systemMessage = {
                    type: 'message',
                    id: 'system-' + Date.now(),
                    text: `Perfect! Now please describe your ${category.name.toLowerCase()} issue or request in the chat box below. You can type your message and press Enter or click the send button.`,
                    sender: 'Support',
                    senderId: 'system',
                    timestamp: new Date(),
                    isMessagedBySupportUser: true,
                    readBy: [],
                    caseKey: '',
                    isSystemMessage: true
                };
                setMessages(prev => [...prev, systemMessage]);
                setTimeout(() => scrollToBottom(), 100);
            }, 500);
        } catch (error) {
            logger(error, 'error', 'DockableChatBox');
            toast.error('Failed to load projects');
        }
    };

    // Handle problem description submission and session creation
    const handleProblemDescriptionSubmit = async (description?: string) => {
        const desc = description || problemDescription;
        if (!desc.trim()) {
            toast.error('Please describe your problem before proceeding.');
            return;
        }

        if (!organizationId || !user?.id || !selectedProject) {
            toast.error('User information not available. Please try logging in again.');
            return;
        }

        setIsCreatingSession(true);

        const casePayload = {

            "description": desc.trim(),
            "summary": desc.slice(0, 100),
            "isSynced": false,
            "organization": {
                "id": userData?.organization?.id || organizationId || '',
                "name": userData?.organization?.name || ''
            },
            "project": {
                "id": selectedProject.id,
                "name": selectedProject.name
            },
            "issueType": {
                "id": "",
                "name": ""
            },
            "priority": {
                "id": "64ccf879d42a294a8fb1cf57",
                "name": "Medium",
                "rank": 0
            },
            "caseType": {
                "id": selectedCategory?.id,
                "name": selectedCategory?.fullName
            },
            "attachmentId": []
        }

        const createCaseResponse = await casesApi.createCase(casePayload);
        if (!(createCaseResponse?.status === 200 || createCaseResponse?.status === 201)) {
            toast.error('Failed to create case. Please try again.');
            setSelectedProject(null);
            setIsCreatingSession(false);
            return;
        }
        if (createCaseResponse?.data) {
            try {
                const sessionPayload = {
                    projectId: selectedProject.id,
                    orgId: organizationId,
                    customerId: user.id,
                    category: selectedCategory?.id,
                    caseKey: createCaseResponse?.data,
                    problemDescription: desc.trim(), // Include the problem description
                };

                const sessionResponse = await chatApi.createSession(sessionPayload);

                if (sessionResponse?.data) {
                    setChatSession(sessionResponse.data);

                    // Add problem description as a chat message to preserve it
                    const problemMessage = {
                        type: 'message',
                        id: 'problem-' + Date.now(),
                        text: desc.trim(),
                        sender: user?.name || 'You',
                        senderId: user?.id || 'user',
                        timestamp: new Date(),
                        isMessagedBySupportUser: false,
                        readBy: [],
                        caseKey: createCaseResponse?.data,
                        isSystemMessage: true
                    };

                    // Add the problem description message to existing messages instead of clearing them
                    setMessages(prev => [...prev, problemMessage]);
                    // setTimeout(() => scrollToBottom(), 100);

                    // Send case creation message through WebSocket after session is created
                    setTimeout(() => {
                        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
                            const messageContent = `Case created successfully! Key: ${createCaseResponse?.data}\nCategory: ${selectedCategory?.name}\nProject: ${selectedProject?.name}\nIssue: ${desc.trim()}\n\nA ${isWithinWorkingHours ? "support agent will be assigned within 15-30 minutes." : " support agent will connect to you during their working hours."}`;
                            const caseCreatedMessageData = {
                                type: 'message',
                                id: 'caseCreated-' + Date.now(),
                                content: messageContent,
                                messagedBy: { name: 'Support', id: 'system' },
                                senderId: 'system', // Required for message to display properly
                                isMessagedBySupportUser: true,
                                timestamp: new Date().toISOString(),
                                sessionId: sessionResponse?.data?.data?.id || sessionResponse?.data?.id,
                                readBy: [],
                                caseKey: createCaseResponse?.data,
                            };
                            wsRef.current.send(JSON.stringify(caseCreatedMessageData));
                        }
                    }, 3000); // Small delay to ensure WebSocket connection is established
                } else {
                    throw new Error('Invalid response from server');
                }
            } catch (error) {
                console.error('Error creating chat session:', error);
                toast.error('Failed to create chat session. Please try again.');
                setSelectedProject(null);
            } finally {
                setIsCreatingSession(false);
            }
        } else {
            toast.error('Failed to create case. Please try again.');
            setSelectedProject(null);
        }
    };

    const getInputFieldState = () => {
        // If we have an existing session, enable input immediately
        if (existingSession) {
            return {
                enabled: isWithinWorkingHours,
                placeholder: isWithinWorkingHours ? "Type your message..." : "Support is outside working hours"
            };
        }

        if (chatSession?.data?.id) {
            return {
                enabled: isWithinWorkingHours,
                placeholder: isWithinWorkingHours ? "Type your message..." : "Support is outside working hours"
            };
        }

        if (showCategorySelector) {
            return {
                enabled: false,
                placeholder: "Please select a category first..."
            };
        }

        // Enable input for problem description
        if (selectedCategory && selectedProject && !chatSession) {
            return {
                enabled: true,
                placeholder: `Describe your ${selectedCategory.name.toLowerCase()} issue here`
            };
        }

        return {
            enabled: false,
            placeholder: "Please complete the setup first..."
        };
    };


    useEffect(() => {
        if (chatSession?.data?.id) {
            chatApi.getMessagesBySessionId(chatSession.data.id, page, pageSize, '-1').then((res) => {
                const transformedArray = res.data[0]?.messages.map(
                    (msg: any) => ({
                        id: msg.id,
                        text: msg.content,
                        sender: msg.messageBy.name,
                        senderId: msg.messageBy.id,
                        timestamp: new Date(msg.createdAt),
                        isMessagedBySupportUser: msg.isMessagedBySupportUser,
                        readBy: msg.readBy,
                        caseKey: chatSession?.data?.caseKey,
                        attachments: msg.attachments,
                    }),
                );

                if (page === 0) {
                    // Preserve existing messages (like greetings, selections) and append new ones
                    setMessages(prev => {
                        const existingIds = new Set(prev.map((msg: Message) => msg.id));
                        const newMessages = transformedArray.reverse().filter(
                            (msg: Message) => !existingIds.has(msg.id),
                        );
                        return [...prev, ...newMessages];
                    });
                    setTimeout(() => scrollToBottom(false), 100);
                } else {
                    const messagesContainer = messagesContainerRef.current;
                    const previousScrollHeight = messagesContainer?.scrollHeight || 0;

                    setMessages((prev) => {
                        const existingIds = new Set(prev.map((msg: Message) => msg.id));
                        const newMessages = transformedArray.filter(
                            (msg: Message) => !existingIds.has(msg.id),
                        );
                        return [...newMessages.reverse(), ...prev];
                    });

                    setTimeout(() => {
                        if (messagesContainer) {
                            const newScrollHeight = messagesContainer.scrollHeight;
                            const scrollDifference = newScrollHeight - previousScrollHeight;
                            messagesContainer.scrollTop = scrollDifference;
                        }
                    }, 0);
                }
            }).catch((error) => {
                console.error('Error fetching messages:', error);
            });
        }
    }, [chatSession, page]);

    // Clear attachments when chat session changes
    useEffect(() => {
        setAttachments([]);
        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current);
            debounceTimeoutRef.current = null;
        }
    }, [chatSession]);

    // // Cleanup timeouts on unmount
    // useEffect(() => {
    //     return debounceTimeoutRef.current && clearTimeout(debounceTimeoutRef.current);
    // }, []);

    useEffect(() => {
        const messagesContainer = messagesContainerRef.current;

        if (messagesContainer) {
            const handleScroll = () => {
                if (messagesContainer.scrollTop === 0) {
                    setPage(prev => prev + 1);
                }
            };

            messagesContainer.addEventListener('scroll', handleScroll);
            return () => {
                messagesContainer.removeEventListener('scroll', handleScroll);
            };
        }
        return undefined;
    }, []);

    const chatSessionId = chatSession?.data?.id ?? chatSession?.id;
    const chatCustomerId = chatSession?.data?.customerId ?? chatSession?.customerId ?? user?.id;
    const chatCaseKey = chatSession?.data?.caseKey ?? chatSession?.caseKey ?? '';

    // WebSocket connection effect
    useEffect(() => {
        const chatBase = process.env.REACT_APP_CHAT;
        if (!chatSessionId || !chatCustomerId || !chatBase) {
            return undefined;
        }

        const wsUrl = `${chatBase.replace(/^http/, 'ws')}/chat/${chatSessionId}/${chatCustomerId}`;
        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;
        let pingInterval: ReturnType<typeof setInterval> | undefined;
        ws.onopen = () => {
            pingInterval = setInterval(() => {
                if (ws.readyState === WebSocket.OPEN) {
                    ws.send(JSON.stringify({ type: "ping" }));
                }
            }, 5000);
        };

        ws.onmessage = (event) => {
            try {
                const messageData = JSON.parse(event.data);
                if (messageData.isMessagedBySupportUser === true && messageData.type === 'message') {
                    setLastSupportUserId(messageData.senderId);
                }
                if (messageData.type === 'typing-start' && messageData.isMessagedBySupportUser === true) {
                    setIsTyping(true);
                } else if (messageData.type === 'typing-stop' && messageData.isMessagedBySupportUser === true) {
                    setIsTyping(false);
                }

                // Only update message state if message has senderId
                if (messageData.senderId) {
                    const newMessage: Message = {
                        id: messageData?.id || 'msg-' + Date.now(),
                        text: messageData.content || messageData.text || messageData.message,
                        sender: messageData.sender || messageData.messagedBy?.name || 'Unknown',
                        senderId: messageData.senderId,
                        isMessagedBySupportUser: messageData.isMessagedBySupportUser || false,
                        readBy: messageData.readBy || [],
                        timestamp: new Date(messageData.timestamp || Date.now()),
                        caseKey: chatCaseKey,
                        attachments: messageData.attachments,
                    };

                    setMessages(prev => [...prev, newMessage]);
                    setTimeout(() => scrollToBottom(), 100);
                } else {
                    setLastReadTime(messageData.lastMessageCreatedAt);
                }
                // Check if this is a lastRead update message

            } catch (error) {
                console.error('Error parsing WebSocket message:', error);
                // Don't add messages to state if they can't be parsed or don't have senderId
            }
        };

        ws.onerror = (error) => {
            logger(error, 'error', 'Chat WebSocket');
            toast.error('Chat connection error');
            if (pingInterval) {
                clearInterval(pingInterval);
            }
        };

        ws.onclose = () => {
            if (pingInterval) {
                clearInterval(pingInterval);
            }
        };

        return () => {
            if (lastReadTimeoutRef.current) {
                clearTimeout(lastReadTimeoutRef.current);
            }
            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current);
            }
            if (wsRef.current && (wsRef.current.readyState === WebSocket.OPEN || wsRef.current.readyState === WebSocket.CONNECTING)) {
                wsRef.current.close();
            }
            if (pingInterval) {
                clearInterval(pingInterval);
            }
        };
    }, [chatSessionId, chatCustomerId, chatCaseKey]);

    const handleAddAttachment = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    }

    const handleFileUpload = async (files: FileList) => {
        if (!files || files.length === 0) return;

        setIsUploading(true);
        try {
            // First, validate all files
            const validFiles = Array.from(files).filter((file) => {
                // Validate file size (2MB limit)
                if (file.size > 2 * 1024 * 1024) {
                    toast.error(`File ${file.name} is too large. Maximum size is 2MB.`);
                    return false;
                }

                // Validate file types
                const allowedTypes = [
                    'image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/bmp', 'image/tiff', 'image/webp',
                    'application/pdf', 'text/plain', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                    'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                ];

                if (!allowedTypes.includes(file.type)) {
                    toast.error(`File type ${file.type} is not supported.`);
                    return false;
                }

                return true;
            });

            // Then upload all valid files in parallel
            const uploadPromises = validFiles.map(async (file, index) => {
                const formData = new FormData();
                formData.append('file', file);

                try {
                    const response = await userApi.uploadAttachments(formData);
                    if (response?.data?.blob_url) {
                        const attachment: Attachment = {
                            id: `att_${Date.now()}_${index}`,
                            name: file.name,
                            size: file.size,
                            type: file.type,
                            url: response.data.blob_url
                        };
                        return attachment;
                    }
                    throw new Error('No blob URL received from upload');
                } catch (error) {
                    console.error('Error uploading file:', error);
                    toast.error(`Failed to upload ${file.name}`);
                    return null;
                }
            });

            const results = await Promise.all(uploadPromises);
            const successfulUploads = results.filter((result) => result !== null) as Attachment[];

            if (successfulUploads.length > 0) {
                setAttachments(prev => [...prev, ...successfulUploads]);
                toast.success(`${successfulUploads.length} file(s) uploaded successfully`);
            }
        } catch (error) {
            console.error('Error in file upload:', error);
            toast.error('Error uploading files');
        } finally {
            setIsUploading(false);
        }
    };

    const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { files } = event.target;
        if (files) {
            handleFileUpload(files);
        }
        // Reset the input value to allow the same file to be selected again
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const removeAttachment = (attachmentId: string) => {
        setAttachments(prev => prev.filter(att => att.id !== attachmentId));
    };

    const getFileIcon = (fileType: string) => {
        if (fileType.startsWith('image/')) {
            return <ImageIcon className={classes.attachmentIcon} />;
        }
        if (fileType === 'application/pdf') {
            return <PictureAsPdfIcon className={classes.attachmentIcon} />;
        }
        if (fileType.startsWith('text/') || fileType.includes('document')) {
            return <DescriptionIcon className={classes.attachmentIcon} />;
        }
        return <InsertDriveFileIcon className={classes.attachmentIcon} />;
    };

    const formatFileSize = (bytes: number) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / (k ** i)).toFixed(2)) + ' ' + sizes[i];
    };

    const downloadAttachment = (attachment: Attachment) => {
        const link = document.createElement('a');
        link.href = attachment.url;
        link.download = attachment.name;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const openAttachmentModal = (attachmentUrl: string, attachmentType?: string) => {
        setSelectedAttachment({ url: attachmentUrl, type: attachmentType });
        setIsAttachmentModalOpen(true);
    };

    const closeAttachmentModal = () => {
        setIsAttachmentModalOpen(false);
        setSelectedAttachment(null);
    };

    const isImageAttachment = (attachment?: { type?: string; url: string }) => {
        if (!attachment) return false;
        if (attachment.type) {
            return attachment.type.startsWith('image/');
        }
        const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.tiff'];
        return imageExtensions.some((ext) => attachment.url?.toLowerCase().includes(ext));
    };

    const renderAttachmentPreview = (attachment: Attachment) => {
        if (attachment.type.startsWith('image/')) {
            return (
                <Box key={attachment.id} className={classes.attachmentPreviewItem}>
                    <Box className={classes.attachmentPreviewInfo}>
                        <IconButton
                            size="small"
                            onClick={() => openAttachmentModal(attachment.url, attachment.type)}
                            style={{
                                padding: 0,
                                minWidth: '40px',
                                height: '40px',
                                borderRadius: '4px',
                                overflow: 'hidden'
                            }}
                            title="Click to view full size"
                        >
                            <img
                                src={attachment.url}
                                alt={attachment.name}
                                style={{
                                    width: '40px',
                                    height: '40px',
                                    objectFit: 'cover',
                                    borderRadius: '4px'
                                }}
                            />
                        </IconButton>
                        <Box style={{ marginLeft: '8px', flex: 1, minWidth: 0 }}>
                            <Typography variant="body2" className={classes.attachmentPreviewName}>
                                {attachment.name}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                                {formatFileSize(attachment.size)}
                            </Typography>
                        </Box>
                    </Box>
                    <Box>
                        <IconButton
                            size="small"
                            onClick={() => openAttachmentModal(attachment.url, attachment.type)}
                            title="View full size"
                        >
                            <ImageIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                            size="small"
                            onClick={() => downloadAttachment(attachment)}
                            title="Download"
                        >
                            <GetAppIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                            size="small"
                            onClick={() => removeAttachment(attachment.id)}
                            title="Remove"
                        >
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    </Box>
                </Box>
            );
        }
        return (
            <Box key={attachment.id} className={classes.attachmentPreviewItem}>
                <Box className={classes.attachmentPreviewInfo}>
                    {getFileIcon(attachment.type)}
                    <Typography variant="body2" className={classes.attachmentPreviewName}>
                        {attachment.name}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                        {formatFileSize(attachment.size)}
                    </Typography>
                </Box>
                <Box>
                    <IconButton
                        size="small"
                        onClick={() => downloadAttachment(attachment)}
                        title="Download"
                    >
                        <GetAppIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                        size="small"
                        onClick={() => removeAttachment(attachment.id)}
                        title="Remove"
                    >
                        <DeleteIcon fontSize="small" />
                    </IconButton>
                </Box>
            </Box>
        );
    };

    const handleDragOver = (event: React.DragEvent) => {
        event.preventDefault();
        event.stopPropagation();
    };

    const handleDragLeave = (event: React.DragEvent) => {
        event.preventDefault();
        event.stopPropagation();
    };

    const handleDrop = (event: React.DragEvent) => {
        event.preventDefault();
        event.stopPropagation();

        const { files } = event.dataTransfer;
        if (files && files.length > 0) {
            handleFileUpload(files);
        }
    };
    const handleTypingStop = () => {
        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify({
                type: 'typing-stop',
                timestamp: new Date().toISOString()
            }));
        }
    };

    const handleSendMessage = () => {

        // Check if input field is enabled before sending message
        if (!getInputFieldState().enabled) {
            toast.error('Please complete the setup process before sending messages.');
            return;
        }

        // Check if there's a message or attachments to send
        if (!inputMessage.trim() && attachments.length === 0) {
            toast.error('Please enter a message or attach a file.');
            return;
        }

        // If we're in the problem description phase, handle it specially
        if (selectedCategory && selectedProject && !chatSession) {
            setProblemDescription(inputMessage.trim());
            handleProblemDescriptionSubmit(inputMessage.trim());
            // Clear the input message after setting the problem description
            setInputMessage('');
            return;
        }

        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
            // Transform attachments to only include blob_url
            const attachmentUrls = attachments.map(attachment => attachment.url);
            const messageData = {
                type: 'message',
                content: inputMessage.trim() || '📎 Attachment(s) shared',
                messagedBy: { name: user?.name, id: user?.id },
                isMessagedBySupportUser: false,
                timestamp: new Date().toISOString(),
                sessionId: chatSessionId,
                readBy: [],
                caseKey: chatCaseKey,
                attachments: attachmentUrls,
            };

            try {
                wsRef.current.send(JSON.stringify(messageData));

                // Clear input and attachments immediately
                setInputMessage('');
                setAttachments([]);

                // Stop typing when message is sent
                if (debounceTimeoutRef.current) {
                    clearTimeout(debounceTimeoutRef.current);
                }
                handleTypingStop();

                // Don't add message to local state - wait for WebSocket confirmation
                // This prevents duplicate messages
            } catch (error) {
                console.error('Error sending message:', error);
                toast.error('Failed to send message');
            }
            return;
        }

        toast.error('Chat is not connected. Please try again.');
    };

    const handleTypingStart = () => {
        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify({
                type: 'typing-start',
                timestamp: new Date().toISOString()
            }));
        }
    };



    const debouncedTyping = () => {
        // Clear existing timeout
        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current);
        }

        // Send typing start immediately
        handleTypingStart();

        // Set timeout to stop typing after 2 seconds of inactivity
        debounceTimeoutRef.current = setTimeout(() => {
            handleTypingStop();
        }, 2000);
    };

    const handleKeyPress = (event: React.KeyboardEvent) => {
        if (event.key === 'Enter' && !event.shiftKey && getInputFieldState().enabled) {
            event.preventDefault();
            // Stop typing when sending message
            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current);
            }
            handleTypingStop();
            handleSendMessage();
        }
    };

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setMenuAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setMenuAnchorEl(null);
    };


    const handleMinimize = () => {
        handleMenuClose();
        setIsMinimized(!isMinimized);
        if (onMinimize) {
            onMinimize();
        }
    };


    const handleEndChat = () => {
        handleMenuClose();

        // Check if there's a support user assigned using lastSupportUserId
        if (!lastSupportUserId) {
            // No support user assigned, directly end the session without feedback
            chatApi.updateSessionStatus(chatSession?.data?.id, 'CLOSE').then(() => {
                toast.success('Chat session closed.');
                onClose();
            }).catch((error) => {
                console.error('Error closing session:', error);
                toast.error('Failed to close session. Please try again.');
            });
        } else {
            // Support user assigned, show feedback collection
            chatApi.updateSessionStatus(chatSession?.data?.id, 'CLOSE').then(() => {
                toast.success('Chat session closed.');
                // onClose();
                setShowFeedbackCollection(true);
            }).catch((error) => {
                console.error('Error closing session:', error);
                toast.error('Failed to close session. Please try again.');
            });
        }
    };

    const handleFeedbackSubmit = async (feedbackData: FeedbackData) => {
        try {
            toast.success('Thank you for your feedback!');
            await chatApi.submitFeedback(feedbackData);
            setShowFeedbackCollection(false);
            onClose();
        } catch (error) {
            console.error('Error submitting feedback:', error);
            toast.error('Failed to submit feedback. Please try again.');
        }
    };

    const handleFeedbackClose = () => {
        setShowFeedbackCollection(false);
        // Still close the chat even if feedback is cancelled
        onClose();
    };

    const handlePaste = async (event: React.ClipboardEvent): Promise<void> => {
        // Check if we're in the problem description phase - don't allow pasting images during this phase
        if (selectedCategory && selectedProject && !chatSession) {
            // User is in problem description phase, don't allow image pasting
            return;
        }

        // Check if input field is enabled for pasting
        if (!getInputFieldState().enabled) {
            return;
        }

        const { clipboardData } = event;
        const { items } = clipboardData;

        // Find the first image item
        let imageFile: File | null = null;
        for (let i = 0; i < items.length; i++) {
            const item = items[i];
            const { type } = item;

            if (type.indexOf('image') !== -1) {
                imageFile = item.getAsFile();
                if (imageFile) {
                    event.preventDefault();
                    break; // Only handle the first image found
                }
            }
        }

        // Process the image file if found
        if (imageFile) {
            // Check if we haven't exceeded attachment limit


            setIsUploading(true);

            try {
                // Use the existing upload logic from handleFileUpload
                const formData = new FormData();
                formData.append('file', imageFile);

                const response = await userApi.uploadAttachments(formData);
                if (response?.data?.blob_url) {
                    const uploadedFile: Attachment = {
                        id: `paste_${Date.now()}`,
                        name: imageFile.name || 'pasted-image.png',
                        size: imageFile.size,
                        type: imageFile.type,
                        url: response.data.blob_url
                    };
                    setAttachments((prev) => [...prev, uploadedFile]);
                    toast.success('Image pasted and uploaded successfully');
                } else {
                    throw new Error('No blob URL received from upload');
                }
            } catch (error) {
                console.error('Error uploading pasted image:', error);
                toast.error('Failed to upload pasted image');
            } finally {
                setIsUploading(false);
            }
        }
    };

    const formatTime = (date: Date) => new Date(date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

    // Helper function to determine input field state

    // Determine what to show in the chat body
    const renderChatBody = () => {
        if (isCreatingSession) {
            return (
                <Box className={classes.loadingContainer}>
                    <CircularProgress />
                    <Typography variant="body2">Creating chat session and case...</Typography>
                </Box>
            );
        }

        if (!chatSession && !existingSession) {
            return (
                <>
                    <Box
                        className={classes.messagesContainer}
                        ref={messagesContainerRef}
                    >
                        {messages.length === 0 ? (
                            <Box className={classes.emptyState}>
                                <SupportAgentIcon style={{ fontSize: '3rem', marginBottom: '1rem' }} />
                                <Typography variant="body1">
                                    Start a conversation with our support team
                                </Typography>
                            </Box>
                        ) : (
                            messages.map((message) => (
                                <Box
                                    key={message.id}
                                    display="flex"
                                    flexDirection="column"
                                    alignItems={message.isMessagedBySupportUser ? "flex-start" : "flex-end"}
                                    justifyContent="flex-start"
                                    gap="8px"
                                    marginLeft={message.isMessagedBySupportUser ? "8px" : "0px"}
                                    marginRight={message.isMessagedBySupportUser ? "0px" : "8px"}
                                >
                                    <Typography variant="body2">{message.sender}</Typography>
                                    <Box
                                        className={`${classes.message} ${message.sender === user?.name ? classes.messageUser : ''
                                            }`}
                                    >
                                        {/* <Avatar style={{ width: '32px', height: '32px', }}>
                                        {message.sender === user?.name ? <Avatar src={user?.image} /> : <SupportAgentIcon />}
                                    </Avatar> */}

                                        <Box style={{ width: '100%', display: 'flex', flexDirection: !message.isMessagedBySupportUser ? 'row-reverse' : 'row' }}>
                                            <Box className={classes.messageBubble}>
                                                <Typography variant="body2" style={{ whiteSpace: 'pre-wrap' }}>
                                                    {message.text}
                                                </Typography>

                                                {/* Display attachments */}
                                                {message.attachments && message.attachments.length > 0 && (
                                                    <Box className={classes.attachmentContainer}>
                                                        {message.attachments.map((attachment, index) => {
                                                            const attachmentUrl = typeof attachment === 'string' ? attachment : attachment.url;
                                                            const attachmentId = typeof attachment === 'string' ? `url_${index}` : attachment.id;

                                                            return (
                                                                <Box
                                                                    key={attachmentId}
                                                                    onClick={() => openAttachmentModal(attachmentUrl)}
                                                                    onKeyDown={(e) => {
                                                                        if (e.key === 'Enter' || e.key === ' ') {
                                                                            e.preventDefault();
                                                                            openAttachmentModal(attachmentUrl);
                                                                        }
                                                                    }}
                                                                    style={{
                                                                        width: '60px',
                                                                        height: '60px',
                                                                        marginRight: '8px',
                                                                        marginBottom: '8px',
                                                                        backgroundColor: '#f5f5f5',
                                                                        border: '1px solid #e0e0e0',
                                                                        borderRadius: '8px',
                                                                        cursor: 'pointer',
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'center',
                                                                        overflow: 'hidden'
                                                                    }}
                                                                    title="Click to view attachment"
                                                                >
                                                                    <InsertDriveFileIcon style={{ fontSize: '24px', color: '#666' }} />
                                                                </Box>
                                                            );
                                                        })}
                                                    </Box>
                                                )}

                                                <Box style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-end', gap: '5px' }}>
                                                    <Typography className={classes.messageTime}>
                                                        {formatTime(message.timestamp)}
                                                    </Typography>
                                                    {!message.isMessagedBySupportUser && (
                                                        message?.readBy?.length > 0 ?
                                                            <DoneAllIcon style={{ fontSize: '14px' }} /> :
                                                            lastReadTime && new Date(lastReadTime) > new Date(message.timestamp) ?
                                                                <DoneAllIcon style={{ fontSize: '14px' }} /> :
                                                                <DoneIcon style={{ fontSize: '14px' }} />
                                                    )}
                                                </Box>
                                            </Box>
                                        </Box>
                                    </Box>
                                </Box>

                            ))
                        )}
                        <div ref={messagesEndRef} />
                    </Box>

                    {/* Interactive Components - Only show when no existing session and no chat session */}
                    {!existingSession && showCategorySelector && (
                        <Box className={classes.interactiveComponent}>
                            <CategorySelector onCategorySelect={handleCategorySelect} />
                        </Box>
                    )}

                    {/* Remove the ProblemDescriptionForm component since users will type directly in chat */}

                    {/* Show working hours notice during selection process if outside working hours */}
                    {!existingSession && !isWithinWorkingHours && (
                        <Box className={classes.workingHoursNotice}>
                            <Typography variant="body2" style={{ fontWeight: 500 }}>
                                {workingHoursMessage}
                            </Typography>
                        </Box>
                    )}

                    {/* Always show the input field, even during selection process */}
                    <Box className={getInputFieldState().enabled ? classes.inputContainer : classes.inputContainerDisabled}>
                        {/* Show helper text for problem description phase */}
                        {!existingSession && selectedCategory && selectedProject && !chatSession && (
                            <Box style={{ marginBottom: '8px', padding: '8px', backgroundColor: '#e3f2fd', borderRadius: '4px', border: '1px solid #2196f3' }}>
                                <Typography variant="body2" style={{ color: '#1976d2', fontSize: '0.875rem' }}>
                                    💬 <strong>Problem Description Phase:</strong> Please describe your issue in detail below. This will help us create your support case.
                                </Typography>
                            </Box>
                        )}

                        {/* Attachment preview */}
                        {attachments.length > 0 && (
                            <Box className={classes.attachmentPreview}>
                                <Typography variant="body2" style={{ marginBottom: '8px', fontWeight: 500 }}>
                                    Attachments ({attachments.length})
                                </Typography>
                                {attachments.map((attachment) => (
                                    <React.Fragment key={attachment.id}>
                                        {renderAttachmentPreview(attachment)}
                                    </React.Fragment>
                                ))}
                            </Box>
                        )}





                        <TextField
                            fullWidth
                            multiline
                            variant="outlined"
                            placeholder={getInputFieldState().placeholder}
                            value={inputMessage}
                            onChange={(e) => {
                                setInputMessage(e.target.value);
                                debouncedTyping();
                            }}
                            onPaste={handlePaste}
                            onKeyPress={handleKeyPress}
                            className={classes.messageInput}
                            minRows={1}
                            maxRows={4}
                            disabled={!getInputFieldState().enabled}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">

                                        <Box >
                                            <IconButton
                                                onClick={handleAddAttachment}
                                                disabled={!getInputFieldState().enabled || (selectedCategory && selectedProject && !chatSession && !existingSession)}
                                                className={classes.attachmentButton}
                                                title="Add attachment"
                                            >
                                                <AttachFileIcon />
                                            </IconButton>
                                            {isUploading && (
                                                <CircularProgress size={20} style={{ marginRight: '8px' }} />
                                            )}
                                            <IconButton
                                                onClick={handleSendMessage}
                                                disabled={!getInputFieldState().enabled || (!inputMessage.trim() && attachments.length === 0)}
                                                title={existingSession ? "Send Message" : (selectedCategory && selectedProject && !chatSession ? "Submit Problem Description" : "Send Message")}
                                            >
                                                <SendIcon />
                                            </IconButton>
                                        </Box>
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Box>
                </>
            );
        }

        if (!chatSession) {
            return (
                <Box className={classes.loadingContainer}>
                    <Typography variant="body2">Initializing chat...</Typography>
                </Box>
            );
        }

        return (
            <>
                <Box
                    className={classes.messagesContainer}
                    ref={messagesContainerRef}
                >
                    {messages.length === 0 ? (
                        <Box className={classes.emptyState}>
                            <SupportAgentIcon style={{ fontSize: '3rem', marginBottom: '1rem' }} />
                            <Typography variant="body1">
                                Start a conversation with our support team
                            </Typography>
                        </Box>
                    ) : (
                        messages.map((message) => (
                            <Box
                                key={message.id}
                                display="flex"
                                flexDirection="column"
                                alignItems={message.isMessagedBySupportUser ? "flex-start" : "flex-end"}
                                justifyContent="flex-start"
                                gap="8px"
                                marginLeft={message.isMessagedBySupportUser ? "8px" : "0px"}
                                marginRight={message.isMessagedBySupportUser ? "0px" : "8px"}
                            >

                                <Typography variant="body2">{message.sender}</Typography>
                                <Box
                                    className={`${classes.message} ${message.sender === user?.name ? classes.messageUser : ''
                                        }`}
                                >
                                    {/* <Avatar style={{ width: '32px', height: '32px', }}>
                                    {message.sender === user?.name ? <Avatar src={user?.image} /> : <SupportAgentIcon />}
                                </Avatar> */}


                                    <Box style={{ width: '100%', display: 'flex', flexDirection: !message.isMessagedBySupportUser ? 'row-reverse' : 'row' }}>

                                        <Box className={classes.messageBubble}>
                                            <Typography variant="body2" style={{ whiteSpace: 'pre-wrap' }}>
                                                {message.text}
                                            </Typography>

                                            {/* Display attachments */}
                                            {message.attachments && message.attachments.length > 0 && (
                                                <Box className={classes.attachmentContainer}>
                                                    {message.attachments.map((attachment, index) => {
                                                        const attachmentUrl = typeof attachment === 'string' ? attachment : attachment.url;
                                                        const attachmentId = typeof attachment === 'string' ? `url_${index}` : attachment.id;

                                                        return (
                                                            <Box
                                                                key={attachmentId}
                                                                onClick={() => openAttachmentModal(attachmentUrl, typeof attachment === 'string' ? undefined : attachment.type)}
                                                                onKeyDown={(e) => {
                                                                    if (e.key === 'Enter' || e.key === ' ') {
                                                                        e.preventDefault();
                                                                        openAttachmentModal(attachmentUrl);
                                                                    }
                                                                }}
                                                                style={{
                                                                    width: '60px',
                                                                    height: '60px',
                                                                    marginRight: '8px',
                                                                    marginBottom: '8px',
                                                                    backgroundColor: '#f5f5f5',
                                                                    border: '1px solid #e0e0e0',
                                                                    borderRadius: '8px',
                                                                    cursor: 'pointer',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    overflow: 'hidden'
                                                                }}
                                                                title="Click to view attachment"
                                                            >
                                                                {attachmentUrl.includes('jpeg') || attachmentUrl.includes('jpg') || attachmentUrl.includes('png') || attachmentUrl.includes('gif') ?
                                                                    <img src={attachmentUrl} alt="File" style={{ width: '100%', height: '100%' }} /> :
                                                                    <InsertDriveFileIcon style={{ fontSize: '24px', color: '#666' }} />
                                                                }
                                                            </Box>
                                                        );
                                                    })}
                                                </Box>
                                            )}

                                            <Box style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-end', gap: '5px' }}>
                                                <Typography className={classes.messageTime}>
                                                    {formatTime(message.timestamp)}
                                                </Typography>
                                                {!message.isMessagedBySupportUser && (
                                                    message?.readBy?.length > 0 ?
                                                        <DoneAllIcon style={{ fontSize: '14px' }} /> :
                                                        lastReadTime && new Date(lastReadTime) > new Date(message.timestamp) ?
                                                            <DoneAllIcon style={{ fontSize: '14px' }} /> :
                                                            <DoneIcon style={{ fontSize: '14px' }} />
                                                )}
                                            </Box>
                                        </Box>
                                    </Box>
                                </Box>
                            </Box>
                        ))
                    )}
                    <div ref={messagesEndRef} />
                </Box>

                {!isWithinWorkingHours && (
                    <Box className={classes.workingHoursNotice}>
                        <Typography variant="body2" style={{ fontWeight: 500 }}>
                            {workingHoursMessage}
                        </Typography>
                    </Box>
                )}

                <Box className={getInputFieldState().enabled ? classes.inputContainer : classes.inputContainerDisabled}>
                    {/* Show helper text for problem description phase */}
                    {selectedCategory && selectedProject && !chatSession && (
                        <Box style={{ marginBottom: '8px', padding: '8px', backgroundColor: '#e3f2fd', borderRadius: '4px', border: '1px solid #2196f3' }}>
                            <Typography variant="body2" style={{ color: '#1976d2', fontSize: '0.875rem' }}>
                                💬 <strong>Problem Description Phase:</strong> Please describe your issue in detail below. This will help us create your support case.
                            </Typography>
                        </Box>
                    )}

                    {/* Attachment preview */}
                    {attachments.length > 0 && (
                        <Box className={classes.attachmentPreview}>
                            <Typography variant="body2" style={{ marginBottom: '8px', fontWeight: 500 }}>
                                Attachments ({attachments.length})
                            </Typography>
                            {attachments.map((attachment) => (
                                <React.Fragment key={attachment.id}>
                                    {renderAttachmentPreview(attachment)}
                                </React.Fragment>
                            ))}
                        </Box>
                    )}

                    {isTyping && <TypingIndicator senderName={user?.name} />}

                    <TextField
                        fullWidth
                        multiline
                        variant="outlined"
                        placeholder={getInputFieldState().placeholder}
                        value={inputMessage}
                        onChange={(e) => {
                            setInputMessage(e.target.value);
                            debouncedTyping();
                        }}
                        onPaste={handlePaste}
                        onKeyPress={handleKeyPress}
                        className={classes.messageInput}
                        minRows={1}
                        maxRows={4}
                        disabled={!getInputFieldState().enabled}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <Box style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-end', gap: '5px' }}>
                                        <IconButton
                                            onClick={handleAddAttachment}
                                            disabled={!getInputFieldState().enabled || (selectedCategory && selectedProject && !chatSession && !existingSession)}
                                            className={classes.attachmentButton}
                                            title="Add attachment"
                                        >
                                            <AttachFileIcon />
                                        </IconButton>
                                        {isUploading && (
                                            <CircularProgress size={20} />
                                        )}
                                        <IconButton
                                            onClick={handleSendMessage}
                                            disabled={(!inputMessage.trim() && attachments.length === 0) || !getInputFieldState().enabled}
                                            title={selectedCategory && selectedProject && !chatSession ? "Submit Problem Description" : "Send Message"}
                                        >
                                            <SendIcon />
                                        </IconButton>
                                    </Box>
                                </InputAdornment>
                            ),
                        }}
                    />
                </Box>
            </>
        );
    };

    return (
        <>
            <Paper
                className={`${classes.chatContainer} ${isMinimized ? classes.chatContainerMinimized : ''
                    }`}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                onDragLeave={handleDragLeave}
            >
                <Box className={classes.chatHeader} onClick={handleMinimize} style={{ cursor: 'pointer' }}>
                    <Box display="flex" alignItems="center">
                        <Typography variant="h6" style={{ fontSize: '12px' }}>
                            {chatSession?.data?.caseKey ? `Support Chat - ${chatSession?.data?.caseKey}` : 'Support Chat'}
                        </Typography>

                    </Box>
                    <Box>



                        <IconButton onClick={handleMinimize}>
                            {isMinimized ? <FullscreenIcon style={{ color: "#ffffff" }} /> : <MinimizeIcon style={{ color: "#ffffff" }} />}
                        </IconButton>
                        <IconButton
                            color="inherit"
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleMenuOpen(e);
                            }}
                            style={{ marginRight: '4px' }}
                            title="Menu"
                        >
                            <MenuIcon />
                        </IconButton>
                    </Box>
                </Box>

                {!isMinimized && (
                    <Box className={classes.chatBody}>
                        {renderChatBody()}
                    </Box>
                )}

                {/* Hidden file input for attachments */}
                <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    className={classes.fileInput}
                    onChange={handleFileInputChange}
                    accept="image/*,.pdf,.txt,.doc,.docx,.xls,.xlsx"
                />
            </Paper>

            {/* Attachment Modal */}
            <Modal
                open={isAttachmentModalOpen}
                onClose={closeAttachmentModal}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                }}
            >
                <Box
                    style={{
                        backgroundColor: 'white',
                        borderRadius: '8px',
                        padding: '20px',
                        maxWidth: '90vw',
                        maxHeight: '90vh',
                        overflow: 'auto',
                        position: 'relative',
                        border: '1px solid #e0e0e0',
                    }}
                >
                    <IconButton
                        onClick={closeAttachmentModal}
                        style={{
                            position: 'absolute',
                            right: '10px',
                            top: '10px',
                            zIndex: 1,
                        }}
                    >
                        {/* <CloseIcon /> */}
                    </IconButton>

                    {selectedAttachment && (
                        isImageAttachment(selectedAttachment) ? (
                            <ImagePreviewer
                                imageUrl={selectedAttachment.url}
                                onClose={closeAttachmentModal}
                            />
                        ) : (
                            <Box
                                style={{
                                    textAlign: 'center',
                                    padding: '20px',
                                }}
                            >
                                <Typography variant="body1" gutterBottom>
                                    This file type is not supported for preview.
                                </Typography>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={() => window.open(selectedAttachment.url, '_blank')}
                                >
                                    Open File
                                </Button>
                            </Box>
                        )
                    )}
                </Box>
            </Modal>

            {/* Burger Menu */}
            <Menu
                anchorEl={menuAnchorEl}
                open={isMenuOpen}
                onClose={handleMenuClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                PaperProps={{
                    style: {
                        maxHeight: 300,
                        zIndex: 999999,
                        width: '200px',
                        marginTop: '8px',
                    },
                }}
            // getContentAnchorEl={null}
            >
                <MenuItem onClick={onClose}>
                    <ListItemIcon>
                        <CloseIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Close Chat" />
                </MenuItem>
                {/* <MenuItem onClick={handleMinimize}>
                    <ListItemIcon>
                        {isMinimized ? <FullscreenIcon fontSize="small" /> : <MinimizeIcon fontSize="small" />}
                    </ListItemIcon>
                    <ListItemText primary={isMinimized ? "Maximize Chat" : "Minimize Chat"} />
                </MenuItem> */}
                {chatSession && <Divider />}
                {chatSession && (
                    <MenuItem onClick={handleEndChat}>
                        <ListItemIcon>
                            <DoDisturbIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary="End Chat Session" />
                    </MenuItem>
                )}



            </Menu>

            {/* Feedback Collection Modal */}
            {showFeedbackCollection && chatSession && (
                <Modal
                    open={showFeedbackCollection}
                    onClose={handleFeedbackClose}
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <>
                        <FeedbackCollection
                            orgId={chatSession?.data?.orgId || ''}
                            projectId={chatSession?.data?.projectId || ''}
                            customerId={chatSession?.data?.customerId || ''}
                            caseKey={chatSession?.data?.caseKey || ''}
                            supportUserId={chatSession?.data?.supportUsers?.[0]?.id || lastSupportUserId || ''}
                            chatSessionId={chatSession?.data?.id || ''}
                            onSubmit={handleFeedbackSubmit}
                            onClose={handleFeedbackClose}
                        />
                    </>
                </Modal>
            )}

        </>
    );
};

export default DockableChatBox; 